import { combineReducers, configureStore, ThunkAction } from '@reduxjs/toolkit'
import { UnknownAction } from 'redux'
import appReducer from './appSlice'
import backgroundReducer from './backgroundSlice'
import dashboardControlReducer from './dashboardControlSlice'
import dashboardDataReducer from './dashboardDataSlice'
import dashboardMwResultReducer from './dashboardMwResultSlice'
import errorReducer from './errorSlice'
import locationReducer from './locationSlice'
import logReducer from './logSlice'
import renderReducer from './appRenderSlice'
import syncReducer from './syncSlice'
import { FormRenderer } from '@fusion-advantage/formrender'
import { backgroundDraftSaveMw } from './backgroundDraftSaveMw'
import { backgroundSyncMw } from './backgroundSyncMw'
import { dashboardMw } from './dashboardMw'
import { locationMw } from './locationMw'
import { storeSettingsMw } from './storeSettingsMw'

const appRootReducer = combineReducers({
	app: appReducer,
	background: backgroundReducer,
	dashboardControl: dashboardControlReducer,
	dashboardData: dashboardDataReducer,
	dashboardMwResult: dashboardMwResultReducer,
	error: errorReducer,
	location: locationReducer,
	log: logReducer,
	render: renderReducer,
	sync: syncReducer,
})

export const createIsolatedAppStore = () => {
	// store these here because they are not serializable and thus
	// not storable in the regular redux state
	const renderInstances = new Map<string, FormRenderer>()
	const store = configureStore({
		reducer: appRootReducer,
		middleware: (getDefault) => {
			let mw = getDefault({
				thunk: { extraArgument: { renderInstances } }
			})
			if(import.meta.env.VITEST) {
				// while running vitest,
				// disable middleware to make it easier to run our SyncThunk tests
				return mw
			}
			return mw.concat(
				backgroundDraftSaveMw,
				backgroundSyncMw,
				dashboardMw,
				locationMw,
				storeSettingsMw,
			)
		}
	})
	return store
}

interface AppMwExtraArgument {
	renderInstances: Map<string, FormRenderer>
}

export type AppStore = ReturnType<typeof createIsolatedAppStore>
export type AppRootState = ReturnType<typeof appRootReducer>
export type AppDispatch = AppStore['dispatch']
// https://redux.js.org/usage/usage-with-typescript#type-checking-redux-thunks
export type AppThunk<ReturnType = void> = ThunkAction<
	ReturnType,
	AppRootState,
	AppMwExtraArgument,
	UnknownAction
>
